import { useEffect, useState } from "react";
import api, { errMsg, fmtRM, useList } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { LogOut, GraduationCap, RefreshCw, ShieldAlert, ArrowRight } from "lucide-react";

function DropoutDialog({ open, onOpenChange, studentId, onDone }) {
  const [reason, setReason] = useState("");
  const [noticeDate, setNoticeDate] = useState(new Date().toISOString().slice(0, 10));
  const [finalFee, setFinalFee] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!reason.trim()) { toast.error("Reason is required"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/admin/students/${studentId}/dropout-request`, {
        reason, notice_date: noticeDate,
        final_month_fee_estimate: finalFee ? parseFloat(finalFee) : null,
      });
      toast.success(`Drop-out scheduled — effective ${r.data.effective_date}`);
      onDone(); onOpenChange(false);
      setReason(""); setFinalFee("");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dropout-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><LogOut className="h-5 w-5 text-amber-600" />Submit drop-out request</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded-md border bg-amber-50 p-3 text-xs leading-relaxed">
            <b>30-day notice rule:</b> The effective date will be the later of <i>notice + 30 days</i> or the <i>end of the next full month</i>. The non-refundable deposit will offset the final month&apos;s fees automatically.
          </div>
          <div><Label className="text-xs">Notice date</Label>
            <Input type="date" value={noticeDate} onChange={(e) => setNoticeDate(e.target.value)} data-testid="dropout-notice-date" /></div>
          <div><Label className="text-xs">Reason *</Label>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="dropout-reason"
              placeholder="e.g. family relocating overseas, schedule conflict, etc." /></div>
          <div><Label className="text-xs">Final-month fee estimate (RM, optional)</Label>
            <Input type="number" step="0.01" value={finalFee} onChange={(e) => setFinalFee(e.target.value)} data-testid="dropout-final-fee"
              placeholder="defaults to deposit balance if blank" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-amber-600 hover:bg-amber-700" data-testid="dropout-submit">
            {busy ? "Submitting…" : "Submit 30-day drop-out notice"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UpgradeDialog({ open, onOpenChange, studentId, onDone }) {
  const { data: tiles } = useList(open ? "/public/courses" : null);
  const [type, setType] = useState("exam_passed");
  const [newCourseId, setNewCourseId] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const allLevels = (tiles || []).flatMap((t) => (t.levels || []).map((lv) => ({
    id: lv.id, label: `${t.name} · ${lv.name}`, fee: lv.fee_from,
  })));

  const submit = async () => {
    if (!newCourseId) { toast.error("Pick the target grade/course"); return; }
    if (type === "exam_passed" && !confirmed) { toast.error("Tick the exam-pass confirmation"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/admin/students/${studentId}/upgrade-grade`, {
        new_course_id: newCourseId, upgrade_type: type,
        exam_pass_confirmation: type === "exam_passed" ? confirmed : false,
        notes: notes || null, deposit_topup: true,
      });
      const d = r.data || {};
      let bits = [`status: ${d.status}`, `effective ${d.effective_date}`];
      if (d.fee_diff !== undefined) bits.push(`fee diff RM ${d.fee_diff.toFixed(2)}`);
      if (d.invoice_action) bits.push(d.invoice_action.replace(/_/g, " "));
      if (d.invoice_id) bits.push(`invoice ${String(d.invoice_id).slice(0, 8)}`);
      if (d.status === "manual_review_required") {
        toast.warning(`Manual review required — ${d.manual_review_reason || "see admin"}`, { duration: 10000 });
      } else {
        toast.success(`Grade upgrade applied — ${bits.join(" · ")}`, { duration: 8000 });
      }
      onDone(); onOpenChange(false);
      setNewCourseId(""); setNotes(""); setConfirmed(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="upgrade-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><GraduationCap className="h-5 w-5 text-secondary" />Upgrade grade / progress to next level</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="flex gap-2">
            {[{ v: "exam_passed", label: "Exam passed (immediate)" }, { v: "non_exam", label: "Progression (notice rule)" }].map((o) => (
              <button key={o.v} onClick={() => setType(o.v)} type="button"
                className={`flex-1 rounded-md border px-3 py-2 text-xs ${type === o.v ? "border-secondary bg-secondary/10 text-secondary font-semibold" : "border-border"}`}
                data-testid={`upgrade-type-${o.v}`}>{o.label}</button>
            ))}
          </div>
          {type === "non_exam" && (
            <div className="rounded-md border bg-amber-50 p-3 text-xs">
              <b>Notice rule:</b> If clicked 1st–24th of the month → minimum 7-day notice, effective on the 1st of the next month.
              If clicked 25th onwards → 30-day notice. New fee starts on the effective date.
            </div>
          )}
          <div><Label className="text-xs">Target grade / course *</Label>
            <Select value={newCourseId} onValueChange={setNewCourseId}>
              <SelectTrigger data-testid="upgrade-target"><SelectValue placeholder="Pick the new level…" /></SelectTrigger>
              <SelectContent className="max-h-[260px]">
                {allLevels.map((l) => <SelectItem key={l.id} value={l.id}>{l.label}{l.fee ? ` (RM ${l.fee.toFixed(2)})` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {type === "exam_passed" && (
            <label className="flex items-start gap-2 cursor-pointer" data-testid="upgrade-confirm-label">
              <input type="checkbox" className="mt-1 h-4 w-4 accent-secondary"
                checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} data-testid="upgrade-exam-confirm" />
              <span className="text-xs">I confirm the student passed the exam and the upgrade should take effect immediately.</span>
            </label>
          )}
          <div><Label className="text-xs">Notes (optional)</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="upgrade-notes" /></div>
          <p className="text-[11px] text-muted-foreground italic">A top-up invoice will be auto-generated for any fee difference, plus a deposit top-up to keep the deposit equal to one month&apos;s tuition.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-secondary hover:bg-[#D96C52]" data-testid="upgrade-submit">
            {busy ? "Submitting…" : "Confirm upgrade"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function StudentLifecycleCard({ studentId, onChanged }) {
  const [data, setData] = useState(null);
  const [dropoutOpen, setDropoutOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  async function load() {
    try {
      const r = await api.get(`/admin/students/${studentId}/lifecycle`);
      setData(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  }
  useEffect(() => { load(); }, [studentId]);

  const applyDropout = async () => {
    if (!data?.dropout_request) return;
    const today = new Date().toISOString().slice(0, 10);
    const earlyForce = today < data.dropout_request.effective_date;
    const msg = earlyForce
      ? `Archive NOW (force)? The drop-out's effective date is ${data.dropout_request.effective_date} — normally the system auto-archives on that day. Forcing now stops lessons immediately. Final-month invoice is already issued.`
      : `Confirm archive — effective date ${data.dropout_request.effective_date} has been reached. Active classes will be archived.`;
    if (!window.confirm(msg)) return;
    try {
      const r = await api.post(`/admin/dropout-requests/${data.dropout_request.id}/apply${earlyForce ? "?force=true" : ""}`);
      const d = r.data || {};
      toast.success(d.already_applied ? "Already archived." : `Archived. Final invoice ${String(d.final_invoice_id || "").slice(0, 8)} · ${d.outstanding_amount > 0.01 ? `RM ${d.outstanding_amount.toFixed(2)} outstanding` : "Fully covered by deposit"}`, { duration: 7000 });
      load(); onChanged && onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const undoDropout = async () => {
    if (!data?.dropout_request) return;
    if (!window.confirm(`Undo this drop-out? This will VOID the final-month invoice and restore the deposit (RM ${(data.dropout_request.deposit_applied || 0).toFixed(2)}). Lessons continue as normal.`)) return;
    try {
      const r = await api.post(`/admin/dropout-requests/${data.dropout_request.id}/undo`);
      toast.success(`Drop-out undone. Final invoice voided. Deposit restored: RM ${(r.data?.deposit_restored || 0).toFixed(2)}.`, { duration: 7000 });
      load(); onChanged && onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!data) return null;
  return (
    <Card className="lg:col-span-2" data-testid="lifecycle-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-secondary" />Lifecycle &amp; deposit
          </h3>
          <div className="text-xs text-muted-foreground">
            Deposit held: <b className="text-foreground">{fmtRM(data.deposit_held)}</b>
          </div>
        </div>

        {data.dropout_request ? (
          <div className="rounded-md border-2 border-amber-500/40 bg-amber-50 p-4 text-sm space-y-2" data-testid="dropout-pending-banner">
            <p className="font-semibold text-amber-700">⚠ Drop-out scheduled — final invoice issued</p>
            <p className="text-xs">Notice: <b>{data.dropout_request.notice_date}</b> · Effective: <b>{data.dropout_request.effective_date}</b> · Auto-archives that day.</p>
            <p className="text-xs">Reason: {data.dropout_request.reason}</p>
            {data.dropout_request.final_invoice_number && (
              <div className="text-xs bg-white/60 rounded p-2 space-y-0.5 border border-amber-200" data-testid="dropout-final-invoice">
                <p>Final-month invoice: <b>{data.dropout_request.final_invoice_number}</b></p>
                <p>Deposit applied: <b>{fmtRM(data.dropout_request.deposit_applied)}</b>
                  {data.dropout_request.top_up_excl_sst > 0 && (
                    <span className="text-amber-700"> · Top-up since registration: {fmtRM(data.dropout_request.top_up_excl_sst)} + SST</span>
                  )}
                </p>
                <p>Outstanding: <b className={data.dropout_request.outstanding_amount > 0.01 ? "text-red-700" : "text-green-700"}>
                  {fmtRM(data.dropout_request.outstanding_amount)}
                </b> {data.dropout_request.outstanding_amount < 0.01 && "(fully covered)"}</p>
              </div>
            )}
            <div className="flex gap-2 flex-wrap pt-1">
              <Button size="sm" variant="outline" onClick={undoDropout} data-testid="undo-dropout-btn">
                Undo drop-out
              </Button>
              <Button size="sm" onClick={applyDropout} className="bg-amber-600 hover:bg-amber-700" data-testid="apply-dropout-btn">
                Archive now (force) <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">No pending drop-out request.</p>
        )}

        {data.grade_upgrades && data.grade_upgrades.length > 0 && (
          <div className="space-y-1">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Recent grade upgrades</p>
            {data.grade_upgrades.slice(0, 3).map((u) => (
              <div key={u.id} className="text-xs py-2 border-b last:border-0" data-testid={`upgrade-row-${u.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="px-1.5 py-0.5 rounded text-[10px] bg-secondary/10 text-secondary font-semibold">{u.upgrade_type}</span>
                  <span className="font-medium">{u.new_course_name}</span>
                  <span className="text-muted-foreground">· effective {u.effective_date}</span>
                  <span className={`ml-auto px-1.5 py-0.5 rounded text-[10px] ${u.status === "applied" ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground"}`}>{u.status}</span>
                </div>
                <div className="text-muted-foreground mt-1 flex gap-3 flex-wrap">
                  {u.fee_diff !== undefined && <span>Monthly fee diff: <b className={u.fee_diff > 0 ? "text-foreground" : ""}>{fmtRM(u.fee_diff)}</b></span>}
                  {u.deposit_diff !== undefined && u.deposit_diff > 0 && <span>Deposit top-up: <b>{fmtRM(u.deposit_diff)}</b></span>}
                  {u.topup_invoice_id ? (
                    <a href="/admin/invoices" className="text-secondary hover:underline font-semibold" data-testid={`upgrade-invoice-link-${u.id}`}>
                      Invoice {String(u.topup_invoice_id).slice(0, 8)}… → Open billing
                    </a>
                  ) : u.status === "applied" ? (
                    <span className="italic text-muted-foreground">No invoice (no fee/deposit change)</span>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-2 border-t">
          <Button variant="outline" size="sm" onClick={() => setUpgradeOpen(true)} data-testid="open-upgrade-dialog">
            <GraduationCap className="h-4 w-4 mr-1.5" />Upgrade grade
          </Button>
          {!data.dropout_request && data.student_status === "active" && (
            <Button variant="outline" size="sm" onClick={() => setDropoutOpen(true)} className="text-amber-700 border-amber-500/40" data-testid="open-dropout-dialog">
              <LogOut className="h-4 w-4 mr-1.5" />Submit drop-out (30-day notice)
            </Button>
          )}
          <Button variant={data.dropout_button_enabled ? "default" : "outline"} size="sm"
                  onClick={async () => {
                    const next = !data.dropout_button_enabled;
                    try {
                      await api.post(`/admin/students/${studentId}/toggle-dropout-button?enabled=${next}`);
                      toast.success(next ? "Parent self-service drop-out ENABLED" : "Parent self-service drop-out disabled");
                      load();
                    } catch (e) { toast.error(errMsg(e)); }
                  }}
                  data-testid="toggle-dropout-button"
                  className={data.dropout_button_enabled ? "bg-emerald-600 hover:bg-emerald-700" : ""}>
            {data.dropout_button_enabled ? "✓ Parent can self-drop-out" : "Allow parent to self-drop-out"}
          </Button>
          <Button variant="ghost" size="sm" onClick={load} data-testid="refresh-lifecycle">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" />Refresh
          </Button>
        </div>
      </CardContent>
      <DropoutDialog open={dropoutOpen} onOpenChange={setDropoutOpen} studentId={studentId}
                     onDone={() => { load(); onChanged && onChanged(); }} />
      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} studentId={studentId}
                     onDone={() => { load(); onChanged && onChanged(); }} />
    </Card>
  );
}
